/**
 * Seal Subscriptions Price Integration
 * 
 * This module integrates Seal Subscriptions app with the Horizon theme's price display system.
 * It listens for Seal Subscriptions API events and updates product prices accordingly.
 * 
 * Features:
 * - Real-time price updates when subscription options change
 * - Currency formatting matching theme settings
 * - Fallback to original prices when subscription is deselected
 * - Full compatibility with existing ProductPrice component
 * - Error handling and debugging support
 */

class SealSubscriptionsPriceIntegration {
  constructor() {
    this.debug = false; // Set to true for debugging
    this.originalPrices = new Map(); // Store original prices for fallback
    this.priceContainers = new Map(); // Cache price containers
    this.initialized = false;
    
    this.init();
  }

  /**
   * Initialize the integration
   */
  init() {
    if (this.initialized) return;
    
    this.log('Initializing Seal Subscriptions Price Integration');
    
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setupEventListeners());
    } else {
      this.setupEventListeners();
    }
    
    this.initialized = true;
  }

  /**
   * Set up event listeners for Seal Subscriptions events
   */
  setupEventListeners() {
    // Listen for subscription widget creation
    document.addEventListener('sealsubs:subscription_widget_created', (event) => {
      this.log('Seal Subscriptions widget created', event.detail);
      this.handleWidgetCreated(event);
    });

    // Listen for price updates from subscription selections
    document.addEventListener('sealsubs:price_update', (event) => {
      this.log('Seal Subscriptions price update', event.detail);
      this.handlePriceUpdate(event);
    });

    // Listen for selling plan changes to handle one-time purchase restoration
    document.addEventListener('sealsubs:selling_plan_changed', (event) => {
      this.log('Seal Subscriptions selling plan changed', event.detail);
      this.handleSellingPlanChanged(event);
    });

    // Listen for price updated events from the theme's product-price component
    document.addEventListener('price:updated', (event) => {
      this.log('Price updated event detected, refreshing Seal Subscriptions');
      this.handlePriceUpdated(event);
    });

    this.log('Event listeners set up successfully');
  }

  /**
   * Handle widget creation event
   * @param {CustomEvent} event - The widget creation event
   */
  handleWidgetCreated(event) {
    try {
      // Cache price containers when widget is created
      this.cachePriceContainers();
      
      // Store original prices for fallback
      this.storeOriginalPrices();
      
      this.log('Widget creation handled successfully');
    } catch (error) {
      this.error('Error handling widget creation:', error);
    }
  }

  /**
   * Handle price update event from Seal Subscriptions
   * @param {CustomEvent} event - The price update event
   */
  handlePriceUpdate(event) {
    try {
      const { price, compareAtPrice, element } = event.detail;
      

      
      if (!price) {
        this.log('No price data in event, skipping update');
        return;
      }

      // Update all product-price elements on the page
      this.updateProductPrices(price, compareAtPrice);
      
      this.log('Price update completed successfully');
    } catch (error) {
      this.error('Error handling price update:', error);
      this.restoreOriginalPrices(); // Fallback on error
    }
  }

  /**
   * Handle selling plan change event
   * @param {CustomEvent} event - The selling plan change event
   */
  handleSellingPlanChanged(event) {
    try {
      const { selling_plan_id } = event.detail;
      
      // If selling_plan_id is empty, restore original prices (one-time purchase)
      if (!selling_plan_id || selling_plan_id === '') {
        this.log('One-time purchase selected, restoring original prices');
        this.restoreOriginalPrices();
        this.restoreSavingsToast();
      }
    } catch (error) {
      this.error('Error handling selling plan change:', error);
    }
  }

  /**
   * Handle price updated events from theme after DOM replacement
   * @param {CustomEvent} event - The price updated event
   */
  handlePriceUpdated(event) {
    try {
      this.log('Handling variant update for Seal Subscriptions');
      
      // Clear stored original prices since variant changed
      this.originalPrices.clear();
      
      // Re-cache price containers for new variant
      this.cachePriceContainers();
      this.storeOriginalPrices();
      
      // Refresh Seal Subscriptions widgets to recalculate for new variant
      this.refreshSealSubscriptionsWidgets();
      
    } catch (error) {
      this.error('Error handling variant update:', error);
    }
  }

  /**
   * Refresh all Seal Subscriptions widgets on the page
   */
  refreshSealSubscriptionsWidgets() {
    try {
      // Call SealSubs.refresh() if available
      if (typeof window.SealSubs !== 'undefined' && typeof window.SealSubs.refresh === 'function') {
        this.log('Refreshing Seal Subscriptions widgets');
        window.SealSubs.refresh();
      } else {
        this.log('SealSubs.refresh() not available, trying alternative refresh method');
        
        // Alternative: Find all Seal Subscriptions widgets and trigger refresh
        const widgets = document.querySelectorAll('.sealsubs-target-element');
        widgets.forEach(widget => {
          // Trigger a refresh by dispatching a custom event
          const refreshEvent = new CustomEvent('sealsubs:force_refresh', {
            detail: { element: widget }
          });
          widget.dispatchEvent(refreshEvent);
        });
      }
    } catch (error) {
      this.error('Error refreshing Seal Subscriptions widgets:', error);
    }
  }

  /**
   * Cache all product-price containers on the page
   */
  cachePriceContainers() {
    const productPriceElements = document.querySelectorAll('product-price');
    
    productPriceElements.forEach((productPrice, index) => {
      const priceContainer = productPrice.querySelector('[ref="priceContainer"]');
      if (priceContainer) {
        this.priceContainers.set(`price-${index}`, {
          productPrice,
          priceContainer
        });
      }
    });

    this.log(`Cached ${this.priceContainers.size} price containers`);
  }

  /**
   * Store original prices for fallback restoration
   */
  storeOriginalPrices() {
    this.priceContainers.forEach((container, key) => {
      const { priceContainer } = container;
      
      // Store the entire HTML content for accurate restoration
      this.originalPrices.set(key, {
        html: priceContainer.innerHTML,
        timestamp: Date.now()
      });
    });

    this.log(`Stored ${this.originalPrices.size} original prices`);
  }

  /**
   * Update product prices with new subscription pricing
   * @param {number} price - New price in cents
   * @param {number} compareAtPrice - Compare at price in cents
   */
  updateProductPrices(price, compareAtPrice) {
    const formattedPrice = this.formatPrice(price);
    const formattedCompareAtPrice = compareAtPrice ? this.formatPrice(compareAtPrice) : null;
    
    this.priceContainers.forEach((container) => {
      const { priceContainer } = container;
      this.updatePriceContainer(priceContainer, formattedPrice, formattedCompareAtPrice);
    });

    // Trigger savings toast update
    this.updateSavingsToast(price, compareAtPrice);
  }

  /**
   * Update a single price container with new pricing
   * @param {HTMLElement} priceContainer - The price container element
   * @param {string} formattedPrice - Formatted price string
   * @param {string|null} formattedCompareAtPrice - Formatted compare at price string
   */
  updatePriceContainer(priceContainer, formattedPrice, formattedCompareAtPrice) {
    try {
      // Check current structure to determine update strategy
      const currentPriceSpan = priceContainer.querySelector('.price');
      const currentCompareAtSpan = priceContainer.querySelector('.compare-at-price');
      
      if (!currentPriceSpan) {
        this.log('No price span found, skipping container');
        return;
      }

      // Update main price
      currentPriceSpan.textContent = formattedPrice;

      // Handle compare at price
      if (formattedCompareAtPrice && formattedCompareAtPrice !== formattedPrice) {
        if (currentCompareAtSpan) {
          // Update existing compare at price
          currentCompareAtSpan.textContent = formattedCompareAtPrice;
          currentCompareAtSpan.style.display = '';
        } else {
          // Create new compare at price element
          this.createCompareAtPriceElement(priceContainer, formattedCompareAtPrice, currentPriceSpan);
        }
      } else if (currentCompareAtSpan) {
        // Hide compare at price if not needed
        currentCompareAtSpan.style.display = 'none';
      }

      this.log('Price container updated successfully');
    } catch (error) {
      this.error('Error updating price container:', error);
    }
  }

  /**
   * Create a compare at price element
   * @param {HTMLElement} priceContainer - The price container
   * @param {string} formattedCompareAtPrice - Formatted compare at price
   * @param {HTMLElement} currentPriceSpan - Current price span for positioning
   */
  createCompareAtPriceElement(priceContainer, formattedCompareAtPrice, currentPriceSpan) {
    const compareAtSpan = document.createElement('s');
    compareAtSpan.className = 'compare-at-price';
    compareAtSpan.textContent = formattedCompareAtPrice;
    
    // Insert before or after price span based on theme's show_sale_price_first setting
    // Default to showing compare at price first (before current price)
    priceContainer.insertBefore(compareAtSpan, currentPriceSpan);
  }

  /**
   * Restore original prices (fallback functionality)
   */
  restoreOriginalPrices() {
    try {
      this.priceContainers.forEach((container, key) => {
        const { priceContainer } = container;
        const originalPrice = this.originalPrices.get(key);
        
        if (originalPrice) {
          priceContainer.innerHTML = originalPrice.html;
          // Dispatch a custom event so other scripts know the DOM has changed
          priceContainer.dispatchEvent(new CustomEvent('price:restored', { bubbles: true }));
        }
      });

      this.log('Original prices restored successfully');
      
      // Trigger savings toast restoration
      this.restoreSavingsToast();
    } catch (error) {
      this.error('Error restoring original prices:', error);
    }
  }

  /**
   * Update savings toast with new pricing information
   * @param {number} price - Current subscription price in cents
   * @param {number} compareAtPrice - Original compare at price in cents (may be null)
   */
  updateSavingsToast(price, compareAtPrice) {
    try {
      // For subscription savings, we need to compare against the one-time purchase price
      // Get the original one-time price from stored data
      let oneTimePrice = null;
      
      // Try to get one-time price from original stored prices
      this.originalPrices.forEach((originalData) => {
        if (originalData.html && !oneTimePrice) {
          // Extract price from original HTML - this represents the one-time purchase price
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = originalData.html;
          const priceSpan = tempDiv.querySelector('.price');
          if (priceSpan) {
            // Extract numeric value from price text
            const priceText = priceSpan.textContent || '';
            // More robust price extraction - handle different currency formats
            const numericMatch = priceText.match(/[\d,]+\.?\d*/);
            if (numericMatch) {
              const cleanPrice = numericMatch[0].replace(/,/g, '');
              oneTimePrice = Math.round(parseFloat(cleanPrice) * 100); // Convert to cents
              this.log('Extracted one-time price:', { priceText, cleanPrice, oneTimePrice });
            }
          }
        }
      });
      
      // For subscription savings, use the original compare-at-price if available, 
      // otherwise fall back to one-time price
      const effectiveCompareAtPrice = compareAtPrice || oneTimePrice;
      
      if (!effectiveCompareAtPrice || effectiveCompareAtPrice <= price) {
        // No savings to show
        const hideEvent = new CustomEvent('savings-toast:hide', {
          detail: { source: 'seal-subscriptions' }
        });
        document.dispatchEvent(hideEvent);
        return;
      }
      
      // Dispatch custom event for savings toast to handle
      const event = new CustomEvent('savings-toast:update', {
        detail: {
          price: price,
          compareAtPrice: effectiveCompareAtPrice,
          source: 'seal-subscriptions'
        }
      });
      

      
      document.dispatchEvent(event);
      this.log('Savings toast update event dispatched', { 
        price, 
        compareAtPrice: effectiveCompareAtPrice,
        oneTimePrice,
        originalCompareAtPrice: compareAtPrice 
      });
    } catch (error) {
      this.error('Error updating savings toast:', error);
    }
  }

  /**
   * Restore savings toast to original state
   */
  restoreSavingsToast() {
    try {
      // Dispatch event to restore savings toast
      const event = new CustomEvent('savings-toast:restore', {
        detail: {
          source: 'seal-subscriptions'
        }
      });
      
      document.dispatchEvent(event);
      this.log('Savings toast restore event dispatched');
    } catch (error) {
      this.error('Error restoring savings toast:', error);
    }
  }

  /**
   * Format price according to theme's currency settings
   * @param {number} priceInCents - Price in cents
   * @returns {string} Formatted price string
   */
  formatPrice(priceInCents) {
    try {
      // Convert cents to currency amount
      const priceAmount = priceInCents / 100;
      
      // Get currency settings from Shopify's global variables
      const currency = window.Shopify?.currency?.active || 'USD';
      const currencyFormat = window.Shopify?.money_format || '${{amount}}';
      
      // Use Shopify's currency formatting if available
      if (window.Shopify && window.Shopify.formatMoney) {
        return window.Shopify.formatMoney(priceInCents, currencyFormat);
      }
      
      // Fallback to basic formatting
      return this.basicPriceFormat(priceAmount, currency);
    } catch (error) {
      this.error('Error formatting price:', error);
      return `$${(priceInCents / 100).toFixed(2)}`;
    }
  }

  /**
   * Basic price formatting fallback
   * @param {number} amount - Price amount
   * @param {string} currency - Currency code
   * @returns {string} Formatted price string
   */
  basicPriceFormat(amount, currency) {
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount);
    } catch (error) {
      // Ultimate fallback
      return `$${amount.toFixed(2)}`;
    }
  }

  /**
   * Logging utility
   * @param {...any} args - Arguments to log
   */
  log(...args) {
    if (this.debug) {
      console.log('[Seal Subscriptions Integration]', ...args);
    }
  }

  /**
   * Error logging utility
   * @param {...any} args - Arguments to log
   */
  error(...args) {
    console.error('[Seal Subscriptions Integration Error]', ...args);
  }

  /**
   * Enable debug mode
   */
  enableDebug() {
    this.debug = true;
    this.log('Debug mode enabled');
  }

  /**
   * Disable debug mode
   */
  disableDebug() {
    this.debug = false;
  }
}

// Initialize the integration when script loads
const sealIntegration = new SealSubscriptionsPriceIntegration();

// Make it available globally for debugging
window.SealSubscriptionsPriceIntegration = sealIntegration;

// Export for module systems if needed
if (typeof module !== 'undefined' && module.exports) {
  module.exports = SealSubscriptionsPriceIntegration;
}